
import Promise from 'bluebird';

import _ from 'underscore';
import URI from 'urijs';
import { awa } from '@bingads-webui/jsll';
import { LoginRequiredError, UndefinedAuthenticationError } from '@bingads-webui-clientcenter/auth-errors';
import GetGreenIdGuid from '@bingads-webui-clientcenter/greenid-util-smart';
// import AddIframe from '@bingads-webui-clientcenter/add-iframe';

import { docDisplayControl as PidlDocDisplayControl, env as pidlEnv } from '@cspayments/pidlsdk/pidlsdk.amd';
import Logger from './logger';
import { PidlErrorKeyI18nMap } from '../consts';
import type {
  AddOrUpdatePiOptions,
  GenericPidlProps,
  GetPidlInstanceOptions,
  PidlError,
  PidlInfo,
  PidlInstance,
  PiData,
  RiskData,
} from '../types';

const defaultLanguage = 'en-US';


function getPidlInfoPromise(this: PidlUtil, piData: PiData, securityAction: string) {
  return this.logger.addTaps(
    'getPidl',
    this.pidlService.getPidlInfo({
      middleTierPiId: piData.MtPiId,
      rurl: piData.ReturnUrl,
      securityAction,
    })
  );
}


function getPidlInfoUPIPromise(this: PidlUtil, piData: PiData, securityAction: string) {
  return this.logger.addTaps(
    'getPidlUPI',
    this.pidlService.getPidlInfoUPI!({
      middleTierPiId: piData.MtPiId,
      customerId: piData.CustomerId,
      accountId: piData.AccountId,
      piid: piData.CtpPiId,
      ctpAccountId: piData.CtpAccountId,
      amount: piData.Amount,
      isPrepay: piData.IsPrepay,
      rurl: piData.ReturnUrl,
      rx: piData.FailureUrl,
      securityAction,
    })
  );
}

function getAuthenticationPromise(this: PidlUtil) {
  const isPidlEnableMsaV2 = _.get(this.permissions, ['dynamic', 'PidlEnableMsaV2'], false);
  this.logger.setPidlOption('authType', this.authenticationType);
  let authenticationPromise;
  switch (this.authenticationType) {
    case 'MSA':
      if (!isPidlEnableMsaV2) {
        authenticationPromise = this.logger.addTaps(
          'auth',
          this.msaAuth.authenticate({
            numTriesAllowed: 3,
          })
        )
          // continue throwing the error so we close the form and don't making more calls as we flush the logs
          .tapCatch(LoginRequiredError, this.msaAuth.handleFullPageRedirect);
      } else {
        authenticationPromise = this.logger.addTaps('auth', this.pifdAuth());
      }
      break;
    case 'AAD':
      authenticationPromise = this.logger.addTaps('auth', this.pifdAuth());
      break;
    case 'Google':
    case 'Facebook':
      // eslint-disable-next-line no-case-declarations
      const requestPayload = { CustomerId: this.cid };
      authenticationPromise = this.logger
        .setOptionsAction('auth', {
          paymentAsAService: true,
          authenticationType: this.authenticationType,
          requestPayload,
        })
        .addTaps('auth', this.paymentAsAServiceApi!.createWalletReq!(requestPayload));
      break;
    default:
      authenticationPromise = Promise.reject(new UndefinedAuthenticationError(this.authenticationType));
  }

  return authenticationPromise;
}


function failureCallback(this: PidlUtil, error: PidlError, errorHandler: (error: PidlError) => void) {
  const errorObj = error && error.error;
  const firstLayerError = errorObj && errorObj.innererror;
  const innerError = firstLayerError && (firstLayerError.innererror || firstLayerError.InnerError);
  const errorCode = innerError && (innerError.code || innerError.ErrorCode);
  const originalPidlErrorMessage = innerError && (innerError.message || innerError.Message);

  const errorKey = `PIDLError_${errorCode}_react`;

  const i18nKey = PidlErrorKeyI18nMap[errorKey as keyof typeof PidlErrorKeyI18nMap];
  let errorMessage;
  // Positional params for placeholders in the i18n string (e.g. {{0}}/{{1}} link tags).
  // getString takes the string key separately, so pass the params array as-is; prepending
  // the (undefined) message here would shift the params and break placeholder substitution.
  const errorMessageFormattingParams = this.pidlErrorFormatMapping[errorKey];

  try {
    errorMessage = this.i18n.getString(
      i18nKey,
      errorMessageFormattingParams,
      {
        preprocess: _.escape,
      }
    );
    // smart signup i18n throws error if value is not defined, this workaround will fix the issue
  } catch (_ignoredValue) {
    errorMessage = undefined;
  }

  this.msaAuth.clearMsaDelegateCookie();
  this.logger.pidlFail(error, { errorCode });

  errorHandler({ errorMessage, errorCode, originalPidlErrorMessage });
}

function getGuid(this: PidlUtil) {
  return new Promise<string>((resolve) => {
    const guid = GetGreenIdGuid(this.GreenIdUrl);
    // TODO: fully deprecate ThreatMetrixUrl with cleanup
    // AddIframe(this.ThreatMetrixUrl.replace('{0}', guid));
    resolve(guid);
  });
}

export class PidlUtil {
  GreenIdUrl: string;
  ThreatMetrixUrl: string;
  authenticationType: GenericPidlProps['authenticationType'];
  cid: number | undefined;
  companyCodeCurrencyCardTypeMapping?: GenericPidlProps['companyCodeCurrencyCardTypeMapping'];
  countryCodeCompanyCodeMapping?: GenericPidlProps['countryCodeCompanyCodeMapping'];
  enabledCountries: Record<string, string[]>;
  excludedCountriesByCard: Record<string, string[]>;
  i18n: GenericPidlProps['i18n'];
  isIntEnv: boolean;
  language: string;
  logger: Logger;
  msaAuth: GenericPidlProps['msaAuth'];
  paymentAsAServiceApi: GenericPidlProps['paymentAsAServiceApi'];
  permissions: GenericPidlProps['permissions'];
  piTypes: Record<string, string>;
  pidlDocParameters: { language: string; partner: string };
  pidlErrorFormatMapping: Record<string, string[]>;
  pidlService: GenericPidlProps['pidlService'];
  pifdAuth: GenericPidlProps['pifdAuth'];
  supportedCardTypes: string[];
  verificationToken: string;
  constructor({
    cid,
    i18n,
    pidlEnvironment,
    enabledCountries,
    excludedCountriesByCard,
    pidlErrorFormatMapping,
    culture,
    supportedCultureMapping,
    authenticationType,
    msaAuth,
    pifdAuth,
    paymentAsAServiceApi,
    pidlService,
    pidlLogger,
    ThreatMetrixUrl,
    GreenIdUrl,
    verificationToken,
    permissions,
    companyCodeCurrencyCardTypeMapping,
    countryCodeCompanyCodeMapping,
  }: GenericPidlProps) {
    pidlEnv.set(pidlEnvironment);
    this.isIntEnv = pidlEnvironment === 'INT';
    this.cid = cid;
    this.i18n = i18n;
    this.enabledCountries = enabledCountries;
    this.excludedCountriesByCard = excludedCountriesByCard;
    this.pidlErrorFormatMapping = pidlErrorFormatMapping;
    this.authenticationType = authenticationType;
    this.msaAuth = msaAuth;
    this.pifdAuth = pifdAuth;
    this.paymentAsAServiceApi = paymentAsAServiceApi;
    this.pidlService = pidlService;
    this.ThreatMetrixUrl = ThreatMetrixUrl;
    this.GreenIdUrl = GreenIdUrl;
    this.verificationToken = verificationToken;
    this.permissions = permissions;
    this.companyCodeCurrencyCardTypeMapping = companyCodeCurrencyCardTypeMapping;
    this.countryCodeCompanyCodeMapping = countryCodeCompanyCodeMapping;


    this.piTypes = _.mapObject(enabledCountries, (_val: string[], key: string) => key);
    this.supportedCardTypes = _.keys(excludedCountriesByCard);
    const lowerCaseCulture = culture.toLowerCase();
    this.language = _.chain(supportedCultureMapping)
      .pairs()
      .filter((s: [string, string]) => s[0].toLowerCase() === lowerCaseCulture)
      .map((a: [string, string]) => a[1])
      .first()
      .value()
      || defaultLanguage;

    let partner = 'bing';
    if (this.authenticationType === 'Google' ||
        this.authenticationType === 'Facebook') {
      if (this.isIntEnv) {
        partner = 'tn_3a5a7d6084a64d4f89eae48c62f1d0f2';
      } else {
        partner = 'tn_080c3be9a04e4eff8ef11090842a7f3d';
      }
    }

    this.pidlDocParameters = {
      language: this.language,
      partner,
    };

    this.logger = new Logger(pidlLogger);

    this.logger.setPidlOption('language', this.language);
  }

  getPidlInstance = ({
    piData,
    securityAction,
    pidlCallbacks,
    errorHandler,
    docDisplayEventHandler,
    displayOptions,
    testHeader,
    supplementalCallbacks,
  }: GetPidlInstanceOptions): Promise<PidlInstance> => {
    const authenticationPromiseBase = getAuthenticationPromise.apply(this);

    const authenticationPromise = Promise.resolve(authenticationPromiseBase).tapCatch((error: unknown) => {
      this.logger.authFail(error, authenticationPromiseBase);
    });


    let pidlInfoPromiseBase: Promise<PidlInfo>;
    if (piData.PiType === 'UPI') {
      pidlInfoPromiseBase = getPidlInfoUPIPromise.apply(this, [piData, securityAction]);
    } else {
      pidlInfoPromiseBase = getPidlInfoPromise.apply(this, [piData, securityAction]);
    }

    const pidlInfoPromise = Promise.resolve(pidlInfoPromiseBase).tapCatch((error: unknown) => {
      this.logger.authFail(error, authenticationPromise, pidlInfoPromiseBase);
    });

    return Promise.all([authenticationPromise, pidlInfoPromise])
      .then((res: [{ token: string }, PidlInfo]) => {
        const authenticationToken = res[0].token;
        const pidlInfo = res[1];
        let successUrl = pidlInfo.SuccessUrl;

        successUrl = new URI(successUrl).setQuery({
          __RequestVerificationToken: this.verificationToken,
          piType: piData.PiType,
          middleTierPiId: piData.MtPiId,
        }).href();

        const correlationVector = !awa.cv.isInit() ? awa.cv.init() : awa.cv.increment();

        this.logger.setPidlOption('correlationVector', correlationVector);

        const telemetryOptions = {
          contextData: {
            cvInitValue: correlationVector,
          },
        };

        const additionalHeaders: Record<string, string> = {
          'ms-cv': correlationVector,
        };

        const isPAAS =
          this.authenticationType === 'Google' ||
          this.authenticationType === 'Facebook';

        if (!isPAAS) {
          additionalHeaders['x-ms-jwt'] = pidlInfo.AuthorizationToken;
        }

        const testHeaders = [];

        if (testHeader) {
          testHeaders.push(testHeader);
        }

        if (this.isIntEnv && isPAAS) {
          testHeaders.push('risk-evaluate-force-approve');
        }

        if (testHeaders.length > 0) {
          additionalHeaders['x-ms-test'] = `{ "scenarios": "${testHeaders.join(',')}", "contact": "bingAdsTest" }`;
        }

        const flights = ['EnableModern', 'EnableModernSepa', 'PXUsePartnerSettingsService', 'PXDisablePSSCache'];

        if (isPAAS) {
          flights.push('PXPaasAddCCDfpIframe');
        } else {
          const isPidl3dsTemplatesEnabled = _.get(this.permissions, ['dynamic', 'Pidl3dsTemplates'], false);
          if (this.isIntEnv) {
            if (isPidl3dsTemplatesEnabled) {
              flights.push('partnerSettingsVersion_16.01.2025-02.33.23-741');
            } else {
              flights.push('partnerSettingsVersion_18.11.2024-18.08.26-021');
            }
          } else {
            // eslint-disable-next-line no-lonely-if
            if (isPidl3dsTemplatesEnabled) {
              flights.push('partnerSettingsVersion_28.04.2025-20.22.55-829');
            } else {
              flights.push('partnerSettingsVersion_18.11.2024-18.08.43-775');
            }
          }
        }

        const isPidlSecureFieldsIsolationAdoptionEnabled = _.get(this.permissions, ['dynamic', 'PidlSecureFieldsIsolationAdoption'], false);
        if (isPidlSecureFieldsIsolationAdoptionEnabled) {
          flights.push(
            'PXEnableSecureFieldAddCreditCard',
            'PXEnableSecureFieldUpdateCreditCard',
            'PXEnableSecureFieldReplaceCreditCard',
            'PXEnableSecureFieldSearchTransaction',
            'PXEnableSecureFieldCvvChallenge',
            'PXEnableSecureFieldIndia3DSChallenge'
          );
        }

        const isPidlEnhanceExpiryDateEnabled = _.get(this.permissions, ['dynamic', 'PidlEnhanceExpiryDate'], false);
        if (isPidlEnhanceExpiryDateEnabled) {
          flights.push(
            'PXCombineExpiryMonthYearToDateTextBox',
            'PXEnableAutoFormatExpiryDate',
            'PXEnableExpiryDateValidationOnClient'
          );
        }

        if (flights.length > 0) {
          additionalHeaders['x-ms-flight'] = flights.join(',');
        }

        const isPidlEnableMsaV2 = _.get(this.permissions, ['dynamic', 'PidlEnableMsaV2'], false);
        if (this.authenticationType === 'MSA' && isPidlEnableMsaV2) {
          additionalHeaders['x-ms-msa-tokentype'] = 'v2';
        }

        const userContext = {
          userId: pidlInfo.UserId,
          authToken: authenticationToken,
          additionalHeaders,
        };

        const returnOptions = {
          urls: {
            success: successUrl,
            failure: pidlInfo.FailureUrl,
          },
          callbacks: {

            success: (response: unknown) => pidlCallbacks.successCallback(response),
            failure: (error: PidlError) => failureCallback.apply(this, [error, errorHandler]),
            gohome: () => pidlCallbacks.goHomeCallback(),
          },
        };

        return new PidlDocDisplayControl(userContext, returnOptions, docDisplayEventHandler, displayOptions, telemetryOptions, supplementalCallbacks);
      })
      .tapCatch((error: unknown) => {
        this.logger.authFail(error, authenticationPromise, pidlInfoPromise);
      });
  };

  getRiskData = (): Promise<RiskData> => this.logger.addTaps(
    'threatCreate',

    getGuid.apply(this).then((guid: string): RiskData => ({
      riskData: {
        greenId: guid,
      },
    }))
  );


  getSupportedCardTypes = (country: string, currency?: string | null) => {
    const isAlignSupportedCardTypesEnabled = _.get(this.permissions, ['dynamic', 'AlignSupportedCardTypes'], false);
    if (isAlignSupportedCardTypesEnabled && this.companyCodeCurrencyCardTypeMapping && this.countryCodeCompanyCodeMapping) {
      const companyCode = this.countryCodeCompanyCodeMapping[country];
      if (companyCode !== undefined) {
        const mapping = this.companyCodeCurrencyCardTypeMapping[companyCode];
        if (mapping && mapping[currency]) {
          const MT_TO_PIDL_CARD_TYPE = {
            VISA: 'visa',
            MC: 'mc',
            AMEX: 'amex',
            NOVUS: 'discover',
            JCB: 'jcb',
          };
          // C+E does not support Discover for companyCode 1062 (USD), filter it out even if MT reports it.
          const isDiscoverExcluded = String(companyCode) === '1062' && currency === 'USD';
          return mapping[currency]
            .map((mtName: string) => MT_TO_PIDL_CARD_TYPE[mtName.toUpperCase() as keyof typeof MT_TO_PIDL_CARD_TYPE] || '')
            .filter((t: string) => t && !(isDiscoverExcluded && t === 'discover'))
            .join(',');
        }
      }
      return '';
    }

    const isJCBCardSupportEnabled = _.get(this.permissions, ['dynamic', 'JCBCardSupport'], false);
    const supportedCardTypesByCountry = _.filter(
      this.supportedCardTypes,

      (cardType: string) => !_.contains(this.excludedCountriesByCard[cardType], country)
    );
    if (country === 'JP' && currency === 'JPY' && isJCBCardSupportEnabled) {
      supportedCardTypesByCountry.push('jcb');
    }
    return supportedCardTypesByCountry.join(',');
  };

  addOrUpdatePI = ({
    rurl,
    psData,
    pi,
    piType,
    mtPiId,
    sessionId,
    addOrUpdateSuccessCallback,
    addOrUpdateFailureCallback,
  }: AddOrUpdatePiOptions) => {
    if (this.authenticationType === 'Google' ||
        this.authenticationType === 'Facebook'
    ) {
      const isGoogleAuthIntlNewSignInPageEnabled = _.get(this.permissions, ['dynamic', 'GoogleAuthIntlNewSignInPage'], false);

      let paasAddPIPayload;
      if (isGoogleAuthIntlNewSignInPageEnabled) {
        paasAddPIPayload = {
          CustomerId: this.cid,
          PaymentInstrumentId: pi.id,
          PaymentRequestId: sessionId,
        };
      } else {
        paasAddPIPayload = {
          CustomerId: this.cid,
          WalletRequestId: pi.response?.requestId,
        };
      }

      const paasAddPIPromise = isGoogleAuthIntlNewSignInPageEnabled ?
        this.paymentAsAServiceApi!.paymentInstrumentAssociateV2!(paasAddPIPayload) :
        this.paymentAsAServiceApi!.associatePaymentInstrument!(paasAddPIPayload);

      this.logger
        .setOptionsAction('addOrUpdatePI', {
          paymentAsAService: true,
          authenticationType: this.authenticationType,
          requestPayload: paasAddPIPayload,
        })
        .addTaps('addOrUpdatePI', paasAddPIPromise)
        .then(addOrUpdateSuccessCallback)
        .catch(addOrUpdateFailureCallback);

      return;
    }

    const paymentSettingsData = psData || {};
    paymentSettingsData.piid = pi.id;
    paymentSettingsData.piType = piType;
    paymentSettingsData.middleTierPiId = mtPiId;
    paymentSettingsData.sessionId = sessionId;

    if (rurl) {
      paymentSettingsData.RURL = rurl;
    }

    this.logger.setOptionsAction('addOrUpdatePI', paymentSettingsData)
      .addTaps(
        'addOrUpdatePI',
        this.pidlService.addOrUpdatePI({
          data: paymentSettingsData,
        })
      )
      .catch(addOrUpdateFailureCallback)
      .then(addOrUpdateSuccessCallback);
  };
}
